import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subject, timer, throwError } from 'rxjs';
import { AuthenticationService } from '../../api/services/authentication.service';
import { environment } from '../../../environments/environment';
import { NotificationService } from 'app/api/services';
import { takeUntil, switchMap, catchError } from 'rxjs/operators';
import { UserSystemAnnouncement } from 'app/api/models';
import { MatDialog } from '@angular/material';
import { SystemInfoDialogComponent } from '../system-info-dialog/system-info-dialog.component';
import { SystemAnnouncementsDialogComponent } from '../system-announcements-dialog/system-announcements-dialog.component';
@Component({
  selector: 'app-header-account',
  templateUrl: './app-header-account.component.html',
  styleUrls: ['./app-header-account.component.css']
})
export class AppHeaderAccountComponent implements OnInit, OnDestroy {
  // Stores the username acquired from the web token
  private username: string;
  // Stores the time-stamp when the user last logged in
  private loginTime: Date;
  // Flag used to determine if the user is logged in
  private loggedFlag: boolean;
  // Defines the interval in which the user login time-stamp automatically upates
  private updateInterval: any;

  private destroy$ = new Subject<void>();
  iotpProfileUrl = environment.iotpProfileUrl;

  //System Announcements
  systemAnnouncements: UserSystemAnnouncement[] = [];
  isAnnouncementLoading = false;
  announcementsDialogOpened = false;

  get unreadAnnouncementCount(): number {
    return this.systemAnnouncements.filter((a) => !a.isRead).length;
  }

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private notificationService: NotificationService,
    public dialog: MatDialog,
    private dialogService: MatDialog
  ) {
    timer(0, 5 * 60 * 1000)
      .pipe(
        switchMap(() => this.notificationService.getUserSystemAnnouncements()),
        takeUntil(this.destroy$)
      )
      .subscribe((announcements) => {
        // console.log('announcements :>> ', announcements);
        this.systemAnnouncements = announcements;

        // Only proceed if dialog hasn't been opened yet
        if (this.announcementsDialogOpened) return;

        // Open dialog if there are unread announcements at first time load
        if (this.unreadAnnouncementCount > 0) {
          this.dialogService.open(SystemAnnouncementsDialogComponent, {
            width: '550px',
            data: this.systemAnnouncements.filter((a) => !a.isRead)
          });
          this.announcementsDialogOpened = true; //lock to let user see announcements only once per session
        }
      });
  }

  ngOnInit(): void {
    this.isLoggedIn();

    // Calls isLoggedIn once every minute
    this.updateInterval = interval(1000);
    this.updateInterval
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => this.isLoggedIn());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Checks the user's local storage to see if the user has their credentials stored in it. */
  isLoggedIn(): void {
    this.loggedFlag = this.authService.isLoggedIn();

    if (this.loggedFlag) {
      this.username = this.authService.getUsername();
      this.loginTime = new Date(this.authService.getLoginTimestamp());
    } else {
      if (this.router.url !== '/login') {
        this.router.navigate(['/login']);
      }
    }
  }

  openNotificationDetailDialog(announcement: UserSystemAnnouncement) {
    this.dialog.open(SystemInfoDialogComponent, {
      width: '450px',
      data: {
        title: announcement.announcementSubject,
        subTitle: announcement.announcementSecondarySubject,
        content: announcement.announcementBody,
        isHtmlContent: true
      }
    });

    this.markSystemAnnouncementsAsRead(announcement);
  }

  markSystemAnnouncementsAsRead(announcement: UserSystemAnnouncement) {
    if (announcement.isRead) {
      return;
    }
    this.notificationService
      .markSystemAnnouncementAsRead(announcement.systemAnnouncementId)
      .pipe(
        switchMap(() => {
          return this.notificationService.getUserSystemAnnouncements();
        }),
        catchError((err) => {
          console.error('Failed to mark announcement as read: ', err);
          return throwError(err);
        })
      )
      .subscribe((announcements) => {
        this.systemAnnouncements = announcements;
      });
  }
}
